import type { UseUploadManagerReturn } from "@wayflyer/flyui";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { usePostHog } from "posthog-js/react";
import { useIntl } from "react-intl";
import { formatDate } from "shared-ui";

import { MESSAGES } from "../banking-accounts/message-definitions";

import type { BankStatementUpload } from "./use-bank-accounts-upload-manager";
import { useBankAccountsUploadManager } from "./use-bank-accounts-upload-manager";
import { useBankStatementExtractionStatuses } from "./use-bank-statement-extraction-statuses";

import type { ExtractionStatusItem } from "./use-bank-statement-extraction-statuses";

type UseBankStatementUploadsProps = {
  customerDomain: string;
  requiredStatementMonths: number;
  onUploadsOrExtractionSettled: () => void;
};

export type BankFileUploadButtonSource = "main_page" | "side_panel";

type UseBankStatementUploadsReturn = Pick<
  UseUploadManagerReturn<BankStatementUpload>,
  "uploads" | "uploadsState" | "onFilesAdded" | "onFileRemoved" | "onFileRetry"
> & {
  batchUploadsState: UseUploadManagerReturn<BankStatementUpload>["uploadsState"];
  batchProgress: { done: number; total: number };
  clearBatch: () => void;
  uploadsInProgress: boolean;
  hasAcceptedUploadThisSession: boolean;
  statementPeriodStart: string;
  registerFilesForGroup: (files: File[], groupUuid: string) => void;
  registerFilesForBatch: (files: File[], source: BankFileUploadButtonSource) => void;
};

export const EXTRACTION_FAILED_METADATA = "extraction-failed";

const isExtractionProcessing = ({ status, exceededMaxPollTime }: ExtractionStatusItem): boolean =>
  !exceededMaxPollTime && status?.extractionState !== "completed" && status?.extractionState !== "failed";

const isExtractionFailed = ({ status, exceededMaxPollTime }: ExtractionStatusItem): boolean =>
  exceededMaxPollTime || status?.extractionState === "failed";

export const useBankStatementUploads = ({
  customerDomain,
  requiredStatementMonths,
  onUploadsOrExtractionSettled,
}: UseBankStatementUploadsProps): UseBankStatementUploadsReturn => {
  const intl = useIntl();
  const posthog = usePostHog();
  const [hasAcceptedUploadThisSession, setHasAcceptedUploadThisSession] = useState(false);
  const [batchFiles, setBatchFiles] = useState<Set<File>>(new Set());

  const statementPeriodStart = useMemo(
    () => formatDate(dayjs().subtract(requiredStatementMonths, "month").toISOString()),
    [requiredStatementMonths]
  );

  const {
    uploads,
    uploadsState,
    onFilesAdded: addFiles,
    onFileRemoved,
    onFileRetry,
    registerFilesForGroup,
    isContextualUpload,
  } = useBankAccountsUploadManager(customerDomain, onUploadsOrExtractionSettled);
  const uploadsInProgress = uploadsState.uploadsStarted && !uploadsState.uploadsComplete;
  const confirmedUploads = uploadsState.uploads.flatMap((upload) => (upload.uploadedFile ? [upload.uploadedFile] : []));

  // Tags the batch for onboarding_bank_file_uploaded; call before onFilesAdded, like registerFilesForGroup.
  const uploadBatchRef = useRef({ source: "main_page" as BankFileUploadButtonSource, startedAt: 0 });
  // Dedupes across settles, since uploadsState.uploads accumulates the whole page session.
  const reportedUploadFilesRef = useRef(new WeakSet<object>());
  const registerFilesForBatch = (files: File[], source: BankFileUploadButtonSource): void => {
    if (files.length > 0 && !uploadsInProgress) {
      uploadBatchRef.current = { source, startedAt: Date.now() };
    }
  };

  const extractionStatusItems = useBankStatementExtractionStatuses(customerDomain, confirmedUploads);
  const processingObjectKeys = new Set(
    extractionStatusItems.filter(isExtractionProcessing).map((item) => item.upload.objectKey)
  );
  // Already attached to its chosen group regardless of outcome, so a contextual upload's failure
  // is silent - it just stays "complete".
  const failedObjectKeys = new Set(
    extractionStatusItems
      .filter(isExtractionFailed)
      .filter((item) => !isContextualUpload(item.upload.objectKey))
      .map((item) => item.upload.objectKey)
  );
  // Unlike failedObjectKeys, doesn't exclude contextual uploads - analytics needs the real outcome.
  const allFailedObjectKeys = new Set(
    extractionStatusItems.filter(isExtractionFailed).map((item) => item.upload.objectKey)
  );

  const processingKeysSignature = [...processingObjectKeys].sort().join(",");
  const previousSignatureRef = useRef("");
  useEffect(() => {
    const previousKeys = previousSignatureRef.current.split(",").filter(Boolean);
    const currentKeys = new Set(processingKeysSignature.split(",").filter(Boolean));
    const justSettled = previousKeys.some((key) => !currentKeys.has(key));
    previousSignatureRef.current = processingKeysSignature;
    if (justSettled) {
      onUploadsOrExtractionSettled();
    }
    // Gated on uploadsInProgress too, since a sibling file still mid-upload hasn't entered
    // processingObjectKeys yet and would otherwise read as an already-settled batch.
    if (!uploadsInProgress && currentKeys.size === 0) {
      reportUploadBatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingKeysSignature, uploadsInProgress, onUploadsOrExtractionSettled, posthog]);

  const displayUploadsState = {
    ...uploadsState,
    uploads: uploadsState.uploads.map((upload) => {
      if (upload.status === "uploading") {
        return { ...upload, processingLabel: intl.formatMessage(MESSAGES.floatingFilesListUploadingLabel) };
      }
      if (upload.status !== "complete" || !upload.uploadedFile) {
        return upload;
      }
      if (processingObjectKeys.has(upload.uploadedFile.objectKey)) {
        return {
          ...upload,
          status: "processing" as const,
          processingLabel: intl.formatMessage(MESSAGES.extractionProcessingText),
          progress: undefined,
        };
      }
      if (failedObjectKeys.has(upload.uploadedFile.objectKey)) {
        return {
          ...upload,
          status: "failed" as const,
          error: intl.formatMessage(MESSAGES.extractionFailedText),
          metadata: EXTRACTION_FAILED_METADATA,
        };
      }
      return upload;
    }),
  };

  const isBatchSettled = !displayUploadsState.uploads.some((upload) =>
    ["queued", "uploading", "processing"].includes(upload.status)
  );
  const batchUploadsState = {
    ...displayUploadsState,
    uploads: displayUploadsState.uploads.filter((_, index) => {
      const file = uploads[index]?.file;
      return file !== undefined && batchFiles.has(file);
    }),
  };
  const batchProgress = {
    done: batchUploadsState.uploads.filter((upload) => ["complete", "failed"].includes(upload.status)).length,
    total: batchUploadsState.uploads.length,
  };
  const clearBatch = () => setBatchFiles(new Set());

  const onFilesAdded: typeof addFiles = (files, rejectedFiles) => {
    if (files.length > 0) {
      posthog?.capture("onboarding_bank_upload_selected", { upload_type: "bank_statement" });
      setHasAcceptedUploadThisSession(true);
    }
    const batchAdditions = [...files, ...(rejectedFiles ?? []).map(({ file }) => file)];
    if (batchAdditions.length > 0) {
      setBatchFiles((previous) => new Set([...(isBatchSettled ? [] : previous), ...batchAdditions]));
    }
    addFiles(files, rejectedFiles);
  };

  const reportUploadBatch = () => {
    // Raw uploads, not displayUploadsState - that hides a contextual upload's extraction failure.
    const newUploads = uploadsState.uploads.filter(
      (upload) => upload.file != null && !reportedUploadFilesRef.current.has(upload.file)
    );
    for (const upload of newUploads) {
      if (upload.file && upload.uploadedFile) {
        reportedUploadFilesRef.current.add(upload.file);
      }
    }
    const ocrTrackedFiles = newUploads.flatMap((upload) => (upload.uploadedFile ? [upload.uploadedFile] : []));
    const verified = ocrTrackedFiles.filter((file) => !allFailedObjectKeys.has(file.objectKey)).length;
    const unverified = ocrTrackedFiles.filter((file) => allFailedObjectKeys.has(file.objectKey)).length;
    if (verified + unverified === 0) {
      return;
    }
    posthog?.capture("onboarding_bank_file_uploaded", {
      upload_type: "bank_statement",
      total_file_count: verified + unverified,
      verified_file_count: verified,
      unverified_file_count: unverified,
      ocr_processing_time: Date.now() - uploadBatchRef.current.startedAt,
      button_source: uploadBatchRef.current.source,
    });
  };

  return {
    uploads,
    uploadsState: displayUploadsState,
    batchUploadsState,
    batchProgress,
    clearBatch,
    onFilesAdded,
    onFileRemoved,
    onFileRetry,
    uploadsInProgress,
    hasAcceptedUploadThisSession,
    statementPeriodStart,
    registerFilesForGroup,
    registerFilesForBatch,
  };
};
